import { capitalize } from 'lodash';
import { Info } from 'luxon';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../clients/axios-instance';
import { Category } from '../../../clients/quipli-service/category/category';
import { DeadDaysConfigHours, DeadDaysConfig } from '../../../clients/quipli-service/pricing/pricing';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import Container from '../../../components/container/container';
import { Button } from '../../../components/form/button';
import { SelectForm } from '../../../components/select-form/select-form';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import { toast } from '../../../util/toasts';
import { CollapsibleDeadDay } from './collapsible';
import './dead-days-config.scss';
import { useCategoryApiV1 } from '../../../clients/quipli-service/category/category-api-v1';

export const DeadDays = () => {
  const { getDeadDaysConfig, upsertDeadDaysConfig } = useAPI();
  const { listCategories } = useCategoryApiV1();
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [deadDaysConfig, setDeadDaysConfig] = useState<DeadDaysConfig>();
  const [hoursConfigState, setHoursConfigState] = useState<DeadDaysConfigHours>();
  const [discardChangesClicked, setDiscardChangesClicked] = useState<boolean>(false);
  useEffect(() => {
    listCategories().then((categories) => {
      const activeCategories = categories.filter((c) => c.isActive && !c.isDeleted);
      setCategories(activeCategories);
      setSelectedCategory(activeCategories[0]);
    });
  }, []);
  useEffect(() => {
    if (selectedCategory) {
      getDeadDaysConfig(selectedCategory.id).then((config) => setDeadDaysConfig(config));
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (deadDaysConfig) {
      setHoursConfigState(deadDaysConfig.hours);
      setDiscardChangesClicked(!discardChangesClicked);
    }
  }, [deadDaysConfig]);

  function isSameConfig() {
    let equal = true;
    if (deadDaysConfig && hoursConfigState) {
      let dow;
      Info.weekdays('short').forEach((d) => {
        dow = d.toLowerCase() as keyof DeadDaysConfigHours;
        if (
          !(
            (!deadDaysConfig.hours[dow] && !hoursConfigState[dow]) ||
            (deadDaysConfig?.hours[dow]?.start === hoursConfigState[dow]?.start &&
              deadDaysConfig?.hours[dow]?.end === hoursConfigState[dow]?.end)
          )
        ) {
          equal = false;
        }
      });
    }
    return equal;
  }
  function saveChanges() {
    if (selectedCategory && hoursConfigState && !isSameConfig()) {
      return upsertDeadDaysConfig({ categoryId: selectedCategory.id, hours: hoursConfigState })
        .then((upserted) => {
          toast('Successfully saved changes', 'success');
          setDeadDaysConfig(upserted);
        })
        .catch((err: ApiError) => {
          const errMsg = err.status === 400 && err.message ? err.message : 'Error saving configurations';
          toast(errMsg, 'error');
        });
    }
  }

  const collapsibleDays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((dow) => {
    if (hoursConfigState)
      return (
        <CollapsibleDeadDay
          title={capitalize(dow)}
          hours={hoursConfigState[dow as keyof DeadDaysConfigHours]}
          setHours={(theHours) =>
            setHoursConfigState({ ...hoursConfigState, [dow as keyof DeadDaysConfigHours]: theHours })
          }
          reset={discardChangesClicked}
        />
      );
  });

  return (
    <div className="my-2 mx-8">
      {categories.length > 0 && deadDaysConfig ? (
        <>
          <div className="mb-4">
            <Typography type={'display-md'}>Pick Up & Drop Off Specifications</Typography>
          </div>

          <Container className="p-4 dead-days-container">
            <Typography type="text-sm" weight="medium">
              Choose category
            </Typography>
            <SelectForm
              className="my-1"
              options={categories}
              value={selectedCategory}
              getOptionLabel={(o) => o.name}
              getOptionValue={(o) => `${o.id}`}
              onChange={(category) => setSelectedCategory(category)}
              isClearable={false}
            />
            <Typography className="text-grey-500" type="text-sm">
              Select which category you'd like to set pick up and drop off specifications for
            </Typography>
          </Container>

          <div className="my-4">
            <Typography type="text-xl" weight="medium">
              Rental pricing based on pick up and drop off times
            </Typography>
            <Typography className="text-grey-500" type="text-sm">
              These price settings will be applied to all products in the selected category
            </Typography>
          </div>

          <Container className="p-4 dead-days-container">
            <div className="grid grid-col gap-3">{collapsibleDays}</div>
          </Container>

          <div className="flex flex-end space-between gap-3 m-4">
            <Button
              className="button-secondary"
              disabled={isSameConfig()}
              onClick={() => {
                setHoursConfigState(deadDaysConfig.hours);
                setDiscardChangesClicked(!discardChangesClicked);
              }}
            >
              Discard
            </Button>
            <Button className="button-primary" disabled={isSameConfig()} onClick={() => saveChanges()}>
              Save
            </Button>
          </div>
        </>
      ) : (
        <Spinner />
      )}
    </div>
  );
};
